import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { 
    CalendarDays, Trash2, Edit, ChevronLeft, 
    MapPin, Plus, Save, AlignLeft, Image as ImageIcon
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

// ==========================================
// STYLED COMPONENTS (WSPÓŁDZIELONE)
// ==========================================

const ManagerContainer = styled.div`
  width: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;

  h2 {
    margin: 0;
    font-size: 1.8rem;
    color: #111;
    font-weight: 800;
  }
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #111;
  color: #fff;
  padding: 10px 18px;
  border: none;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;

  &:hover { background: #333; transform: translateY(-2px); }
  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
`;

const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  color: #111;
  padding: 10px 18px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover { background: #f4f6f8; }
`;

// --- KARTY FORMULARZY ---

const FormCard = styled.form`
  background: #fff;
  border-radius: 12px;
  padding: 30px;
  border: 1px solid #eee;
  box-shadow: 0 4px 15px rgba(0,0,0,0.03);
  margin-bottom: 30px;
`;

const FormSectionTitle = styled.h4`
  margin: 0 0 20px 0;
  font-size: 1.1rem;
  color: #111;
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  svg { color: #888; }
`;

const FormGroupGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  label { font-size: 0.9rem; font-weight: 700; color: #555; }
  input {
    padding: 12px 14px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 1rem;
    font-family: inherit;
    transition: border-color 0.3s;
    &:focus { outline: none; border-color: #111; }
  }
`;

// --- WIDOK LISTY (SIATKA KAFELKÓW) ---

const EventsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const EventCard = styled.div`
  background: #fff;
  border-radius: 12px;
  border: 1px solid #eee;
  box-shadow: 0 4px 15px rgba(0,0,0,0.03);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.06);
  }

  .image-wrapper {
    width: 100%;
    height: 180px;
    background: #f4f6f8;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    svg {
      color: #ccc;
      width: 40px;
      height: 40px;
    }
  }

  .content {
    padding: 25px;
    display: flex;
    flex-direction: column;
    flex: 1;

    h3 {
      margin: 0 0 15px 0;
      font-size: 1.3rem;
      font-weight: 800;
      color: #111;
      line-height: 1.2;
    }

    .info-row {
      display: flex;
      align-items: center;
      gap: 10px;
      color: #666;
      font-size: 0.95rem;
      margin-bottom: 8px;

      svg { width: 18px; height: 18px; color: #999; flex-shrink: 0; }
      strong { color: #111; }
    }
  }

  .actions {
    display: flex;
    gap: 10px;
    padding: 15px 25px;
    border-top: 1px solid #f0f0f0;
    background: #fafafa;

    button {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 5px;
      padding: 10px;
      border: none;
      border-radius: 6px;
      font-weight: 600;
      cursor: pointer;
      transition: 0.2s;
    }

    .btn-edit { background: #eef2ff; color: #234ffc; }
    .btn-edit:hover { background: #dce5ff; }

    .btn-delete { background: #fff0f0; color: #dc3545; }
    .btn-delete:hover { background: #ffe0e0; }
  }
`;

// ==========================================
// GŁÓWNY KOMPONENT
// ==========================================

export default function EventsManager() {
    const [events, setEvents] = useState([]);
    
    // URL Params
    const [searchParams, setSearchParams] = useSearchParams();
    const viewState = searchParams.get("action") || "list";
    const editingId = searchParams.get("id") || null;

    const [loading, setLoading] = useState(false);
    
    const [formData, setFormData] = useState({ nazwa: "", data: "", adres: "", zdjecie: "" });
    const [imageFile, setImageFile] = useState(null); 
    
    const dataLoadedRef = useRef(null);
    const fileInputRef = useRef(null); // Referencja do czyszczenia pola "Wybierz plik"

    const adresAPI = "https://api.pth-hokej.pl";

    const fetchEvents = async () => {
        try {
            const res = await fetch(`${adresAPI}/api/scheduledevents`);
            const data = await res.json();
            setEvents(data);
        } catch (err) {
            console.error("Błąd pobierania wydarzeń:", err);
        }
    };

    useEffect(() => { 
        fetchEvents(); 
    }, []);

    // AUTO-WYPEŁNIANIE PO ODŚWIEŻENIU (F5)
    useEffect(() => {
        if (viewState === "edit" && editingId && events.length > 0) {
            if (dataLoadedRef.current !== editingId) {
                const item = events.find(e => e._id === editingId);
                if (item) {
                    const formattedDate = item.data ? new Date(item.data).toISOString().split('T')[0] : "";
                    
                    setFormData({ 
                        nazwa: item.nazwa || "", 
                        data: formattedDate, 
                        adres: item.adres || "",
                        zdjecie: item.zdjecie || ""
                    });
                    setImageFile(null); 
                    if (fileInputRef.current) fileInputRef.current.value = ""; // Czyszczenie inputa po odświeżeniu
                    dataLoadedRef.current = editingId;
                }
            }
        } else if (viewState === "list") {
            dataLoadedRef.current = null;
        }
    }, [viewState, editingId, events]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // NOWA FUNKCJA DO CZYSZCZENIA ZDJĘCIA
    const handleRemoveImage = () => {
        setImageFile(null);
        setFormData((prev) => ({ ...prev, zdjecie: "" }));
        if (fileInputRef.current) {
            fileInputRef.current.value = ""; // Fizyczne wyczyszczenie pola wyboru pliku
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const url = viewState === "edit" ? `${adresAPI}/api/scheduledevents/${editingId}` : `${adresAPI}/api/scheduledevents`;
            const method = viewState === "edit" ? "PUT" : "POST";

            const submitData = new FormData();
            submitData.append("nazwa", formData.nazwa);
            submitData.append("data", formData.data);
            submitData.append("adres", formData.adres);
            
            // Dodajemy plik lub link, a jeśli nic nie ma (bo usunięto), wysyłamy puste zdjęcie by nadpisać stare
            if (imageFile) {
                submitData.append("zdjeciePlik", imageFile);
            } else if (formData.zdjecie) {
                submitData.append("zdjecie", formData.zdjecie);
            } else {
                submitData.append("zdjecie", ""); 
            }

            await fetch(url, {
                method,
                body: submitData, 
            });

            await fetchEvents();
            setSearchParams({});
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Na pewno chcesz usunąć to wydarzenie?")) return;
        try {
            await fetch(`${adresAPI}/api/scheduledevents/${id}`, { method: "DELETE" });
            fetchEvents();
        } catch (err) {
            console.error(err);
        }
    };

    const handleAddClick = () => {
        setFormData({ nazwa: "", data: "", adres: "", zdjecie: "" });
        setImageFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setSearchParams({ action: "add" });
    };

    const handleEditClick = (item) => {
        const formattedDate = item.data ? new Date(item.data).toISOString().split('T')[0] : "";
        setFormData({ nazwa: item.nazwa || "", data: formattedDate, adres: item.adres || "", zdjecie: item.zdjecie || "" });
        setImageFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        dataLoadedRef.current = item._id;
        setSearchParams({ action: "edit", id: item._id });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleBackToList = () => setSearchParams({});

    return (
        <ManagerContainer>
            
            {/* WIDOK LISTY WYDARZEŃ */}
            {viewState === "list" && (
                <>
                    <HeaderRow>
                        <h2>Zarządzanie Wydarzeniami</h2>
                        <PrimaryButton onClick={handleAddClick}>
                            <Plus size={20} /> Dodaj Wydarzenie
                        </PrimaryButton>
                    </HeaderRow>

                    {events.length === 0 ? (
                        <p style={{ color: "#666" }}>Brak nadchodzących wydarzeń w systemie.</p>
                    ) : (
                        <EventsGrid>
                            {events.map((ev) => (
                                <EventCard key={ev._id}>
                                    <div className="image-wrapper">
                                        {ev.zdjecie ? (
                                            <img src={ev.zdjecie} alt={ev.nazwa} />
                                        ) : (
                                            <CalendarDays />
                                        )}
                                    </div>
                                    <div className="content">
                                        <h3>{ev.nazwa}</h3>
                                        <div className="info-row">
                                            <CalendarDays /> 
                                            <span><strong>{new Date(ev.data).toLocaleDateString('pl-PL')}</strong></span>
                                        </div>
                                        <div className="info-row">
                                            <MapPin /> 
                                            <span>{ev.adres}</span>
                                        </div>
                                    </div>
                                    <div className="actions">
                                        <button className="btn-edit" onClick={() => handleEditClick(ev)}>
                                            <Edit size={16} /> Edytuj
                                        </button>
                                        <button className="btn-delete" onClick={() => handleDelete(ev._id)}>
                                            <Trash2 size={16} /> Usuń
                                        </button>
                                    </div>
                                </EventCard>
                            ))}
                        </EventsGrid>
                    )}
                </>
            )}

            {/* WIDOK FORMULARZA (DODAWANIE / EDYCJA) */}
            {(viewState === "add" || viewState === "edit") && (
                <>
                    <HeaderRow>
                        <SecondaryButton onClick={handleBackToList}>
                            <ChevronLeft size={20} /> Wróć do listy
                        </SecondaryButton>
                        <h2>{viewState === "edit" ? "Edycja Wydarzenia" : "Nowe Wydarzenie"}</h2>
                    </HeaderRow>

                    <FormCard onSubmit={handleSubmit}>
                        
                        <FormSectionTitle><AlignLeft size={20}/> Informacje o wydarzeniu</FormSectionTitle>
                        
                        <FormGroup style={{ marginBottom: '20px' }}>
                            <label>Nazwa wydarzenia</label>
                            <input 
                                required 
                                name="nazwa" 
                                placeholder="np. Turniej Mikołajkowy w Poznaniu" 
                                value={formData.nazwa} 
                                onChange={handleChange} 
                            />
                        </FormGroup>

                        <FormGroupGrid>
                            <FormGroup>
                                <label>Data wydarzenia</label>
                                <input 
                                    required 
                                    type="date"
                                    name="data" 
                                    value={formData.data} 
                                    onChange={handleChange} 
                                />
                            </FormGroup>
                            <FormGroup>
                                <label>Dokładny Adres</label>
                                <input 
                                    required 
                                    name="adres" 
                                    placeholder="np. ul. Chwiałkowskiego 34, Poznań" 
                                    value={formData.adres} 
                                    onChange={handleChange} 
                                />
                            </FormGroup>
                        </FormGroupGrid>
                        
                        <FormSectionTitle><ImageIcon size={20} /> Zdjęcie do wydarzenia (opcjonalne)</FormSectionTitle>
                        <FormGroup style={{ background: '#fafafa', padding: '20px', borderRadius: '8px', border: '1px solid #eee' }}>
                            
                            {/* Nagłówek z przyciskiem Usuń (widoczny tylko gdy jest wybrane zdjęcie) */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <label style={{ margin: 0 }}>Wybierz źródło zdjęcia (jedna z opcji)</label>
                                {(imageFile || formData.zdjecie) && (
                                    <button 
                                        type="button" 
                                        onClick={handleRemoveImage}
                                        style={{ 
                                            display: 'flex', alignItems: 'center', gap: '5px', 
                                            background: '#ffe0e0', color: '#dc3545', border: 'none', 
                                            padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', 
                                            fontWeight: 'bold', fontSize: '0.85rem' 
                                        }}
                                    >
                                        <Trash2 size={16} /> Usuń przypisane zdjęcie
                                    </button>
                                )}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', alignItems: 'center' }}>
                                <div>
                                    <span style={{ fontSize: '0.85rem', color: '#888', display: 'block', marginBottom: '8px' }}>Opcja 1: Wgraj zdjęcie z komputera</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={fileInputRef}
                                        onChange={(e) => setImageFile(e.target.files[0])}
                                        style={{ padding: '8px', background: '#fff', width: '100%', boxSizing: 'border-box' }}
                                        disabled={!!formData.zdjecie}
                                    />
                                </div>

                                <div>
                                    <span style={{ fontSize: '0.85rem', color: '#888', display: 'block', marginBottom: '8px' }}>Opcja 2: Wklej link do zdjęcia z internetu</span>
                                    <input
                                        name="zdjecie"
                                        value={formData.zdjecie}
                                        onChange={handleChange}
                                        placeholder="https://..."
                                        disabled={!!imageFile}
                                        style={{ width: '100%', boxSizing: 'border-box' }}
                                    />
                                </div>
                            </div>
                        </FormGroup>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
                            <PrimaryButton type="submit" disabled={loading}>
                                <Save size={20} /> {viewState === "edit" ? "Zapisz Zmiany" : "Dodaj Wydarzenie"}
                            </PrimaryButton>
                        </div>
                    </FormCard>
                </>
            )}
        </ManagerContainer>
    );
}